import {
  BILLING_FREQUENCY_OPTIONS,
  formatBillingPeriod as formatBillingPeriodByFrequency,
  type BillingFrequency,
} from "./billingPeriodUtils";
import { extractPaginatedList } from "./billingPaginationUtils";
import type { BillingConfigurationApiRecord } from "./billingListUtils";
import { formatDisplayDate } from "./paymentStatusUtils";
import type { InvoiceStatus } from "./types";
import { inr } from "./utils";
import PTSNavigationRoutes from "../../../utils/navigationRoutes";

export const INVOICE_REMINDER_QUERY_KEY = ["invoice-reminder"] as const;

export interface InvoiceReminderApiItem {
  billing_type?: string;
  frequency?: string | null;
  binning_start_date?: string | null;
  amount?: string | number | null;
  currency?: string | null;
  hsn_sac?: string | null;
  qty?: number | string | null;
  cgst?: string | number | null;
  sgst?: string | number | null;
  igst?: string | number | null;
}

export interface InvoiceReminderApiRecord {
  id?: string;
  invoice_number?: string;
  invoice_title?: string | null;
  client_name?: string | null;
  project?: string | null;
  project_name?: string | null;
  items?: InvoiceReminderApiItem[];
  next_reminder_date?: string | null;
  reminder?: number | string | null;
  grace_period?: number | string | null;
  grand_total?: string | number | null;
  pending_amount?: string | number | null;
  status?: string | null;
  client_billing_email?: string | null;
  cc_emails?: string[] | null;
  accountant_review_email?: string | null;
  notes?: string | null;
  invoice_date?: string | null;
  billing_period_from?: string | null;
  billing_period_to?: string | null;
  frequency?: string | null;
  updated_on?: string | null;
  created_on?: string | null;
}

export interface InvoiceReminderRecord {
  id: string;
  invoiceNumber: string;
  client: string;
  project?: string;
  nextReminderDate: string;
  nextReminderDateDisplay: string;
  reminderDays: number;
  gracePeriod: number;
  total: number;
  pending: number;
  status: InvoiceStatus;
  email: string;
  invoiceDate: string;
  billingPeriod: string;
  frequency: string;
  source: InvoiceReminderApiRecord;
}

function isLikelyUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    value.trim()
  );
}

function resolveInvoiceDisplayNumber(
  invoiceNumber?: string | null,
  id?: string | null
): string {
  const candidates = [invoiceNumber, id].filter(
    (value): value is string =>
      typeof value === "string" && value.trim().length > 0
  );

  for (const candidate of candidates) {
    const value = candidate.trim();
    if (!isLikelyUuid(value)) {
      return value;
    }
  }

  return "";
}

export function formatReminderTitle(record: InvoiceReminderRecord): string {
  if (record.invoiceNumber) {
    return `Invoice ${record.invoiceNumber} · ${record.client}`;
  }

  return record.client;
}

function parseAmount(value?: string | number | null): number {
  if (value === undefined || value === null || value === "") {
    return 0;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseCount(value?: string | number | null): number {
  if (value === undefined || value === null || value === "") {
    return 0;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeDateKey(value?: string | null): string {
  if (!value?.trim()) {
    return "";
  }

  return value.trim().split("T")[0];
}

function formatBillingPeriodRange(
  from?: string | null,
  to?: string | null
): string {
  const fromValue = from?.trim();
  const toValue = to?.trim();

  if (fromValue && toValue) {
    return `${formatDisplayDate(fromValue)} – ${formatDisplayDate(toValue)}`;
  }

  if (fromValue) {
    return formatDisplayDate(fromValue);
  }

  if (toValue) {
    return formatDisplayDate(toValue);
  }

  return "—";
}

export function getTomorrowDateKey(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const year = tomorrow.getFullYear();
  const month = String(tomorrow.getMonth() + 1).padStart(2, "0");
  const day = String(tomorrow.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function getTomorrowDisplayDate(): string {
  return formatDisplayDate(getTomorrowDateKey());
}

function compareReminderRecords(
  a: InvoiceReminderRecord,
  b: InvoiceReminderRecord
): number {
  const dateCompare = b.nextReminderDate.localeCompare(a.nextReminderDate);
  if (dateCompare !== 0) {
    return dateCompare;
  }

  return b.total - a.total;
}

function resolveFrequencyValue(
  frequency?: string | null
): BillingFrequency | null {
  if (!frequency?.trim()) {
    return null;
  }

  const normalized = frequency.trim().toLowerCase();
  const match = BILLING_FREQUENCY_OPTIONS.find(
    option =>
      option.value === normalized ||
      option.label.trim().toLowerCase() === normalized
  );

  return match?.value ?? null;
}

function sumItemsAmount(items?: InvoiceReminderApiItem[]): number {
  if (!Array.isArray(items) || items.length === 0) {
    return 0;
  }

  return items.reduce((sum, item) => sum + parseAmount(item.amount), 0);
}

function resolveItemBillingPeriod(item?: InvoiceReminderApiItem): string {
  if (!item) {
    return "—";
  }

  const startDate = item.binning_start_date?.trim();
  const frequencyValue = resolveFrequencyValue(item.frequency);

  if (startDate && frequencyValue) {
    return formatBillingPeriodByFrequency(startDate, frequencyValue);
  }

  if (startDate) {
    return formatDisplayDate(startDate);
  }

  return "—";
}

function resolveReminderInvoiceDate(
  record: InvoiceReminderApiRecord,
  primaryItem?: InvoiceReminderApiItem
): string {
  if (record.invoice_date?.trim()) {
    return formatDisplayDate(record.invoice_date.trim());
  }

  const itemStartDate = primaryItem?.binning_start_date?.trim();
  if (itemStartDate) {
    return formatDisplayDate(itemStartDate);
  }

  const createdOn = normalizeDateKey(record.created_on);
  if (createdOn) {
    return formatDisplayDate(createdOn);
  }

  return "—";
}

function resolveReminderBillingPeriod(
  record: InvoiceReminderApiRecord,
  primaryItem?: InvoiceReminderApiItem
): string {
  const fromPeriod = formatBillingPeriodRange(
    record.billing_period_from,
    record.billing_period_to
  );

  if (fromPeriod !== "—") {
    return fromPeriod;
  }

  return resolveItemBillingPeriod(primaryItem);
}

function resolveReminderFrequency(
  record: InvoiceReminderApiRecord,
  primaryItem?: InvoiceReminderApiItem
): string {
  return resolveFrequencyLabel(primaryItem?.frequency ?? record.frequency);
}

function resolveFrequencyLabel(frequency?: string | null): string {
  if (!frequency?.trim()) {
    return "—";
  }

  const normalized = frequency.trim().toLowerCase();
  const match = BILLING_FREQUENCY_OPTIONS.find(
    option =>
      option.value === normalized ||
      option.label.trim().toLowerCase() === normalized
  );

  return match?.label ?? frequency.trim();
}

function normalizeInvoiceStatus(status?: string | null): InvoiceStatus {
  const value = status?.trim().toLowerCase() ?? "";

  if (value === "paid") {
    return "Paid";
  }
  if (value === "sent") {
    return "Sent";
  }
  if (value === "overdue") {
    return "Overdue";
  }
  if (value === "draft") {
    return "Draft";
  }
  if (value === "on hold" || value === "on_hold" || value === "onhold") {
    return "On Hold";
  }

  return "Pending";
}

export function normalizeInvoiceReminderRecord(
  record: InvoiceReminderApiRecord
): InvoiceReminderRecord | null {
  const id = record.id?.trim();
  const nextReminderDate = normalizeDateKey(record.next_reminder_date);

  if (!id || !nextReminderDate) {
    return null;
  }

  const primaryItem = Array.isArray(record.items) ? record.items[0] : undefined;
  const totalFromItems = sumItemsAmount(record.items);
  const total =
    totalFromItems > 0 ? totalFromItems : parseAmount(record.grand_total);
  const pending = parseAmount(record.pending_amount);

  return {
    id,
    invoiceNumber: resolveInvoiceDisplayNumber(
      record.invoice_number,
      record.id
    ),
    client: record.client_name?.trim() ?? "—",
    project: record.project_name?.trim() ?? record.project?.trim() ?? undefined,
    nextReminderDate,
    nextReminderDateDisplay: formatDisplayDate(nextReminderDate),
    reminderDays: parseCount(record.reminder),
    gracePeriod: parseCount(record.grace_period),
    total,
    pending: pending > 0 ? pending : total,
    status: normalizeInvoiceStatus(record.status),
    email: record.client_billing_email?.trim() ?? "—",
    invoiceDate: resolveReminderInvoiceDate(record, primaryItem),
    billingPeriod: resolveReminderBillingPeriod(record, primaryItem),
    frequency: resolveReminderFrequency(record, primaryItem),
    source: record,
  };
}

export function mapInvoiceReminderToBillingConfiguration(
  record: InvoiceReminderApiRecord
): BillingConfigurationApiRecord {
  const primaryItem = Array.isArray(record.items) ? record.items[0] : undefined;

  return {
    id: record.id,
    invoice_number: record.invoice_number,
    billing_type: primaryItem?.billing_type,
    frequency: primaryItem?.frequency ?? record.frequency ?? undefined,
    binning_start_date: primaryItem?.binning_start_date ?? undefined,
    items: Array.isArray(record.items)
      ? record.items.map(item => ({
          amount: item.amount ?? undefined,
          currency: item.currency ?? undefined,
          hsn_sac: item.hsn_sac ?? undefined,
          qty:
            item.qty === undefined || item.qty === null
              ? undefined
              : Number(item.qty),
          cgst: item.cgst ?? undefined,
          sgst: item.sgst ?? undefined,
          igst: item.igst ?? undefined,
          billing_type: item.billing_type,
          frequency: item.frequency ?? undefined,
          binning_start_date: item.binning_start_date ?? undefined,
        }))
      : undefined,
    client_billing_email: record.client_billing_email ?? undefined,
    cc_emails: Array.isArray(record.cc_emails)
      ? record.cc_emails.filter(
          (email): email is string => typeof email === "string"
        )
      : undefined,
    accountant_review_email: record.accountant_review_email ?? undefined,
    reminder: parseCount(record.reminder),
    grace_period: parseCount(record.grace_period),
    next_reminder_date: record.next_reminder_date ?? undefined,
    notes: record.notes ?? undefined,
    project: record.project ?? undefined,
    client_name: record.client_name ?? undefined,
    status: record.status ?? undefined,
    created_on: record.created_on ?? undefined,
    updated_on: record.updated_on ?? undefined,
  };
}

export function getBillingConfigurationEditPath(billingId: string): string {
  const params = new URLSearchParams({
    tab: "config",
    editBilling: billingId,
    editNonce: String(Date.now()),
  });
  return `${PTSNavigationRoutes.BILLING_INVOICE}?${params.toString()}`;
}

export function extractInvoiceReminderRecords(
  response: unknown
): InvoiceReminderApiRecord[] {
  return extractPaginatedList(response, [
    "results",
    "data",
    "data_listing",
    "reminder_listing",
  ]).filter(
    (item): item is InvoiceReminderApiRecord =>
      Boolean(item) && typeof item === "object"
  );
}

export function normalizeInvoiceReminderList(
  response: unknown
): InvoiceReminderRecord[] {
  return extractInvoiceReminderRecords(response)
    .map(normalizeInvoiceReminderRecord)
    .filter((record): record is InvoiceReminderRecord => record !== null)
    .sort(compareReminderRecords);
}

export function getTomorrowLatestReminder(
  reminders: InvoiceReminderRecord[]
): InvoiceReminderRecord | null {
  const tomorrow = getTomorrowDateKey();
  const tomorrowReminders = reminders.filter(
    reminder => reminder.nextReminderDate === tomorrow
  );

  if (tomorrowReminders.length === 0) {
    return null;
  }

  return [...tomorrowReminders].sort(compareReminderRecords)[0] ?? null;
}

export function formatReminderAmount(record: InvoiceReminderRecord): string {
  const amount = record.pending > 0 ? record.pending : record.total;
  return inr(amount);
}
